import jsPDF from 'jspdf';

// ============================================================================
// TYPES ET INTERFACES
// ============================================================================

export interface PDFExtractionOptions {
  filename: string;
  type: 'company' | 'financial-institution';
  title: string;
  subtitle?: string;
}

interface FormField {
  label: string;
  value: any;
  type: 'text' | 'select' | 'multiselect' | 'checkbox' | 'textarea' | 'date' | 'number' | 'email' | 'tel' | 'list' | 'complex' | 'currency' | 'person' | 'asset' | 'stock' | 'location' | 'socialLinks';
  width?: 'full' | 'half' | 'third';
  options?: Array<{value: string, label: string}>;
  required?: boolean;
}

interface SectionConfig {
  title: string;
  fields: FormField[];
  columns?: 1 | 2 | 3;
}

// ============================================================================
// CONSTANTES DE MISE EN PAGE
// ============================================================================

const COLORS = {
  primary: { r: 25, g: 124, b: 168 },      // #197ca8
  secondary: { r: 100, g: 100, b: 100 },
  text: { r: 40, g: 40, b: 40 },
  textLight: { r: 120, g: 120, b: 120 },
  border: { r: 200, g: 200, b: 200 },
  background: { r: 250, g: 250, b: 250 },
  success: { r: 34, g: 139, b: 34 },
  warning: { r: 255, g: 165, b: 0 },
};

const LAYOUT = {
  pageWidth: 210,
  pageHeight: 297,
  margin: 15,
  headerHeight: 25,
  footerHeight: 15,
  sectionGap: 8,
  fieldGap: 4,
  lineHeight: 4,
};

// ============================================================================
// CLASSE PRINCIPALE
// ============================================================================

class UltimatePDFGenerator {
  private pdf: jsPDF;
  private currentY: number = 0;
  private pageNumber: number = 0;
  private formType: string = '';
  // formStatus utilisé pour le suivi interne
  private contentWidth: number;

  constructor() {
    this.pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    this.contentWidth = LAYOUT.pageWidth - 2 * LAYOUT.margin;
  }

  // ==========================================================================
  // UTILITAIRES DE BASE
  // ==========================================================================

  private setColor(color: {r: number, g: number, b: number}, type: 'text' | 'draw' | 'fill' = 'text') {
    if (type === 'text') this.pdf.setTextColor(color.r, color.g, color.b);
    else if (type === 'draw') this.pdf.setDrawColor(color.r, color.g, color.b);
    else this.pdf.setFillColor(color.r, color.g, color.b);
  }

  private getNestedValue(obj: any, path: string): any {
    if (!obj || !path) return null;
    return path.split('.').reduce((curr, key) => curr?.[key] ?? null, obj);
  }

  private formatValue(value: any, type: string): string {
    if (value === null || value === undefined || value === '') return '';
    
    if (type === 'checkbox') return value ? '☑ Oui' : '☐ Non';
    if (type === 'date' && value) {
      try { return new Date(value).toLocaleDateString('fr-FR'); } 
      catch { return String(value); }
    }
    if (type === 'currency' && typeof value === 'number') {
      return value.toLocaleString('fr-FR') + ' USD';
    }
    if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '';
    if (typeof value === 'object') return this.formatComplexValue(value);
    
    return String(value);
  }

  private formatComplexValue(obj: any): string {
    if (!obj) return '';
    if (obj.nom && obj.prenoms) return `${obj.nom} ${obj.prenoms}${obj.fonction ? ` - ${obj.fonction}` : ''}`;
    if (obj.designation) return `${obj.designation}${obj.type ? ` (${obj.type})` : ''}`;
    if (obj.platform && obj.url) return `${obj.platform}: ${obj.url}`;
    if (obj.address) return obj.address;
    if (obj.label) return obj.label;
    if (obj.name) return obj.name;
    return JSON.stringify(obj).substring(0, 50);
  }

  private getLabelForValue(value: any, options?: Array<{value: string, label: string}>): string {
    if (!options || !value) return String(value || '');
    const opt = options.find(o => o.value === value);
    return opt ? opt.label : String(value);
  }

  // ==========================================================================
  // GESTION DES PAGES
  // ==========================================================================

  private addNewPage(sectionTitle?: string) {
    if (this.pageNumber > 0) {
      this.addFooter();
      this.pdf.addPage();
    }
    this.pageNumber++;
    this.currentY = LAYOUT.margin;
    this.addHeader(sectionTitle);
  }

  private addHeader(sectionTitle?: string) {
    // Ligne supérieure
    this.setColor(COLORS.primary, 'draw');
    this.pdf.setLineWidth(0.5);
    this.pdf.line(LAYOUT.margin, this.currentY, LAYOUT.pageWidth - LAYOUT.margin, this.currentY);
    
    this.currentY += 3;
    
    // Type de formulaire à gauche
    this.pdf.setFontSize(7);
    this.pdf.setFont('helvetica', 'normal');
    this.setColor(COLORS.textLight);
    const formLabel = this.formType === 'company' ? 'ENTREPRISE' : 'INSTITUTION FINANCIÈRE';
    this.pdf.text(formLabel, LAYOUT.margin, this.currentY + 3);
    
    // Numéro de page à droite
    this.pdf.text(`Page ${this.pageNumber}`, LAYOUT.pageWidth - LAYOUT.margin - 15, this.currentY + 3);
    
    this.currentY += 8;
    
    // Titre de section
    if (sectionTitle) {
      this.pdf.setFontSize(11);
      this.pdf.setFont('helvetica', 'bold');
      this.setColor(COLORS.primary);
      this.pdf.text(sectionTitle, LAYOUT.margin, this.currentY);
      
      this.currentY += 2;
      this.setColor(COLORS.primary, 'draw');
      this.pdf.setLineWidth(0.3);
      this.pdf.line(LAYOUT.margin, this.currentY, LAYOUT.pageWidth - LAYOUT.margin, this.currentY);
      this.currentY += LAYOUT.sectionGap;
    }
  }

  private addFooter() {
    const footerY = LAYOUT.pageHeight - LAYOUT.footerHeight;
    
    this.setColor(COLORS.border, 'draw');
    this.pdf.setLineWidth(0.2);
    this.pdf.line(LAYOUT.margin, footerY, LAYOUT.pageWidth - LAYOUT.margin, footerY);
    
    this.pdf.setFontSize(6);
    this.setColor(COLORS.textLight);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text('© 2025 i-kiotaHub - Wanzo Land', LAYOUT.margin, footerY + 5);
    this.pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 
      LAYOUT.pageWidth - LAYOUT.margin - 50, footerY + 5);
  }

  private checkPageBreak(neededHeight: number = 20): boolean {
    const maxY = LAYOUT.pageHeight - LAYOUT.footerHeight - LAYOUT.margin;
    if (this.currentY + neededHeight > maxY) {
      this.addNewPage();
      return true;
    }
    return false;
  }

  // ==========================================================================
  // PAGE DE COUVERTURE
  // ==========================================================================

  private generateCoverPage(options: PDFExtractionOptions, entityName: string, isBlank: boolean) {
    this.pageNumber++;
    
    // Logo WANZO
    this.currentY = 60;
    this.pdf.setFontSize(32);
    this.pdf.setFont('helvetica', 'bold');
    this.setColor(COLORS.primary);
    const logoText = 'WANZO';
    const logoWidth = this.pdf.getTextWidth(logoText);
    this.pdf.text(logoText, (LAYOUT.pageWidth - logoWidth) / 2, this.currentY);
    
    // Sous-titre
    this.currentY += 12;
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'normal');
    this.setColor(COLORS.secondary);
    const subText = 'Plateforme d\'accélération d\'entreprise';
    const subWidth = this.pdf.getTextWidth(subText);
    this.pdf.text(subText, (LAYOUT.pageWidth - subWidth) / 2, this.currentY);
    
    // Ligne décorative
    this.currentY += 15;
    this.setColor(COLORS.primary, 'draw');
    this.pdf.setLineWidth(0.5);
    this.pdf.line(LAYOUT.margin + 40, this.currentY, LAYOUT.pageWidth - LAYOUT.margin - 40, this.currentY);
    
    // Nom de l'entité
    if (!isBlank && entityName) {
      this.currentY += 25;
      this.pdf.setFontSize(18);
      this.pdf.setFont('helvetica', 'bold');
      this.setColor(COLORS.primary);
      const nameLines = this.pdf.splitTextToSize(entityName, this.contentWidth - 40);
      nameLines.forEach((line: string) => {
        const lineWidth = this.pdf.getTextWidth(line);
        this.pdf.text(line, (LAYOUT.pageWidth - lineWidth) / 2, this.currentY);
        this.currentY += 8;
      });
    }
    
    // Type de formulaire
    this.currentY += 15;
    this.pdf.setFontSize(12);
    this.pdf.setFont('helvetica', 'normal');
    this.setColor(COLORS.text);
    const typeText = options.type === 'company' 
      ? 'FORMULAIRE D\'IDENTIFICATION ENTREPRISE'
      : 'FORMULAIRE D\'IDENTIFICATION INSTITUTION FINANCIÈRE';
    const typeWidth = this.pdf.getTextWidth(typeText);
    this.pdf.text(typeText, (LAYOUT.pageWidth - typeWidth) / 2, this.currentY);
    
    // Statut
    this.currentY += 8;
    this.pdf.setFontSize(10);
    this.setColor(isBlank ? COLORS.warning : COLORS.success);
    const statusText = isBlank ? '[ FORMULAIRE VIERGE ]' : '[ FORMULAIRE COMPLÉTÉ ]';
    const statusWidth = this.pdf.getTextWidth(statusText);
    this.pdf.text(statusText, (LAYOUT.pageWidth - statusWidth) / 2, this.currentY);
    
    // Encadré d'information
    this.currentY += 30;
    this.setColor(COLORS.primary, 'draw');
    this.pdf.setLineWidth(0.3);
    this.pdf.roundedRect(LAYOUT.margin + 20, this.currentY, this.contentWidth - 40, 35, 3, 3);
    
    this.currentY += 8;
    this.pdf.setFontSize(8);
    this.setColor(COLORS.text);
    const notice = options.type === 'company'
      ? 'Ce formulaire permet l\'enregistrement de votre entreprise sur la plateforme Wanzo pour l\'accès aux services de financement, d\'investissement et d\'accompagnement.'
      : 'Ce formulaire permet l\'enregistrement de votre institution financière pour collaborer avec l\'écosystème Wanzo et accéder aux opportunités de partenariat.';
    const noticeLines = this.pdf.splitTextToSize(notice, this.contentWidth - 50);
    noticeLines.forEach((line: string) => {
      this.pdf.text(line, LAYOUT.margin + 25, this.currentY);
      this.currentY += 5;
    });
    
    // Date de génération
    this.currentY = LAYOUT.pageHeight - 50;
    this.pdf.setFontSize(9);
    this.setColor(COLORS.textLight);
    this.pdf.text(`Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 
      LAYOUT.margin, this.currentY);
    
    this.addFooter();
  }

  // ==========================================================================
  // RENDU DES CHAMPS - Suite dans la partie 2
  // ==========================================================================

  // Méthode pour dessiner un titre de sous-section
  private drawSubSectionTitle(title: string) {
    this.checkPageBreak(15);
    this.currentY += 3;
    
    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'bold');
    this.setColor(COLORS.primary);
    this.pdf.text(title, LAYOUT.margin, this.currentY);
    
    this.setColor(COLORS.border, 'draw');
    this.pdf.setLineWidth(0.1);
    this.pdf.line(LAYOUT.margin, this.currentY + 1.5, LAYOUT.pageWidth - LAYOUT.margin, this.currentY + 1.5);
    
    this.currentY += 6;
  }

  // Méthode pour dessiner un champ simple
  private drawField(field: FormField, x: number, width: number): number {
    const startY = this.currentY;
    let fieldHeight = 10;
    
    // Calculer la hauteur selon le type
    if (field.type === 'textarea') fieldHeight = 18;
    else if (field.type === 'complex' || field.type === 'person' || field.type === 'asset' || field.type === 'stock') {
      fieldHeight = this.calculateComplexFieldHeight(field.value);
    }
    else if (field.type === 'list' || field.type === 'multiselect') {
      fieldHeight = Math.max(10, (Array.isArray(field.value) ? field.value.length : 1) * 4 + 6);
    }
    
    // Label
    this.pdf.setFontSize(7);
    this.pdf.setFont('helvetica', 'bold');
    this.setColor(COLORS.text);
    this.pdf.text(field.label + (field.required ? ' *' : ''), x, this.currentY);
    
    // Zone de saisie
    const fieldY = this.currentY + 2.5;
    this.setColor(COLORS.border, 'draw');
    this.pdf.setLineWidth(0.15);
    this.setColor(COLORS.background, 'fill');
    this.pdf.roundedRect(x, fieldY, width, fieldHeight - 3, 1, 1, 'FD');
    
    // Valeur
    this.pdf.setFontSize(7);
    this.pdf.setFont('helvetica', 'normal');
    this.setColor(COLORS.text);
    
    this.renderFieldValue(field, x + 2, fieldY + 3, width - 4);
    
    return startY + fieldHeight + 2;
  }

  private calculateComplexFieldHeight(value: any): number {
    if (!value) return 10;
    if (Array.isArray(value)) {
      return Math.max(10, value.length * 8 + 6);
    }
    return 12;
  }

  private renderFieldValue(field: FormField, x: number, y: number, maxWidth: number): void {
    if (field.value === null || field.value === undefined || field.value === '') {
      this.pdf.setFontSize(6);
      this.pdf.setFont('helvetica', 'italic');
      this.setColor(COLORS.textLight);
      this.pdf.text('À compléter...', x, y);
      return;
    }

    this.pdf.setFontSize(7);
    this.pdf.setFont('helvetica', 'normal');
    this.setColor(COLORS.text);

    switch (field.type) {
      case 'checkbox':
        this.pdf.text(field.value ? '☑ Oui' : '☐ Non', x, y);
        break;

      case 'select':
        const label = this.getLabelForValue(field.value, field.options);
        this.pdf.text(label, x, y);
        break;

      case 'multiselect':
      case 'list':
        if (Array.isArray(field.value) && field.value.length > 0) {
          let currentY = y;
          field.value.slice(0, 5).forEach((item) => {
            const itemText = typeof item === 'string' ? item : this.getLabelForValue(item, field.options);
            this.pdf.text(`• ${itemText}`, x, currentY);
            currentY += 3.5;
          });
          if (field.value.length > 5) {
            this.pdf.setFont('helvetica', 'italic');
            this.pdf.text(`... et ${field.value.length - 5} autre(s)`, x, currentY);
          }
        }
        break;

      case 'complex':
      case 'person':
      case 'asset':
      case 'stock':
      case 'location':
        this.renderComplexField(field.value, x, y, maxWidth);
        break;

      case 'socialLinks':
        if (Array.isArray(field.value)) {
          let currentY = y;
          field.value.slice(0, 4).forEach(link => {
            this.pdf.text(`${link.platform}: ${link.url}`, x, currentY);
            currentY += 3.5;
          });
        }
        break;

      case 'textarea':
        const lines = this.pdf.splitTextToSize(String(field.value), maxWidth);
        this.pdf.text(lines.slice(0, 4), x, y);
        break;

      default:
        const text = this.formatValue(field.value, field.type);
        if (this.pdf.getTextWidth(text) > maxWidth) {
          const truncated = this.pdf.splitTextToSize(text, maxWidth)[0];
          this.pdf.text(truncated, x, y);
        } else {
          this.pdf.text(text, x, y);
        }
    }
  }

  private renderComplexField(value: any, x: number, y: number, maxWidth: number) {
    if (!value) return;
    
    let currentY = y;
    
    if (Array.isArray(value)) {
      value.slice(0, 4).forEach((item, index) => {
        const summary = this.getComplexItemSummary(item, index + 1);
        const lines = this.pdf.splitTextToSize(summary, maxWidth);
        this.pdf.text(lines[0], x, currentY);
        currentY += 3.5;
      });
      if (value.length > 4) {
        this.pdf.setFont('helvetica', 'italic');
        this.setColor(COLORS.textLight);
        this.pdf.text(`... et ${value.length - 4} autre(s)`, x, currentY);
      }
    } else if (typeof value === 'object') {
      const summary = this.getComplexItemSummary(value, 0);
      this.pdf.text(summary, x, currentY);
    }
  }

  private getComplexItemSummary(item: any, index: number): string {
    if (!item) return '';
    
    // PersonData
    if (item.nom && item.prenoms) {
      const name = `${item.nom} ${item.prenoms}`;
      const role = item.fonction ? ` - ${item.fonction}` : '';
      const percent = item.pourcentageActions ? ` (${item.pourcentageActions}%)` : '';
      return `${index}. ${name}${role}${percent}`;
    }
    
    // AssetData
    if (item.designation && item.type) {
      const value = item.valeurActuelle ? ` - ${item.valeurActuelle.toLocaleString()} ${item.devise || 'USD'}` : '';
      return `${index}. ${item.designation} (${item.type})${value}`;
    }
    
    // StockData
    if (item.designation && item.quantiteStock !== undefined) {
      return `${index}. ${item.designation}: ${item.quantiteStock} ${item.unite || 'unités'}`;
    }
    
    // Location
    if (item.address || item.latitude) {
      return item.address || `Lat: ${item.latitude}, Long: ${item.longitude}`;
    }
    
    // Social Link
    if (item.platform && item.url) {
      return `${item.platform}: ${item.url}`;
    }
    
    // Generic
    if (item.label) return `${index}. ${item.label}`;
    if (item.name) return `${index}. ${item.name}`;
    
    return `${index}. Élément configuré`;
  }

  // ==========================================================================
  // RENDU DES SECTIONS
  // ==========================================================================

  private renderSection(section: SectionConfig, getValue: (key: string) => any) {
    this.checkPageBreak(20);
    this.drawSubSectionTitle(section.title);
    
    const columns = section.columns || 2;
    const gap = 5;
    const colWidth = (this.contentWidth - (columns - 1) * gap) / columns;
    
    let col = 0;
    let rowStartY = this.currentY;
    let maxRowY = this.currentY;
    
    section.fields.forEach((field) => {
      // Déterminer la largeur du champ
      let fieldWidth = colWidth;
      let fieldCols = 1;
      
      if (field.width === 'full' || field.type === 'textarea' || field.type === 'complex' || 
          field.type === 'person' || field.type === 'asset' || field.type === 'stock') {
        fieldWidth = this.contentWidth;
        fieldCols = columns;
      } else if (field.width === 'half' && columns > 2) {
        fieldWidth = colWidth * 2 + gap;
        fieldCols = 2;
      }
      
      // Nouvelle ligne si nécessaire
      if (col + fieldCols > columns) {
        col = 0;
        this.currentY = maxRowY;
        rowStartY = maxRowY;
      }
      
      // Vérifier le saut de page
      this.checkPageBreak(15);
      if (col === 0) {
        rowStartY = this.currentY;
        maxRowY = this.currentY;
      }
      
      // Calculer la position X
      const x = LAYOUT.margin + col * (colWidth + gap);
      
      // Appliquer la valeur
      const fieldWithValue: FormField = {
        ...field,
        value: field.value !== undefined ? field.value : getValue(field.label.toLowerCase().replace(/[^a-z]/g, ''))
      };
      
      // Dessiner le champ
      this.currentY = rowStartY;
      const newY = this.drawField(fieldWithValue, x, fieldWidth);
      maxRowY = Math.max(maxRowY, newY);
      
      // Avancer les colonnes
      col += fieldCols;
      if (col >= columns) {
        col = 0;
        this.currentY = maxRowY;
      }
    });
    
    // S'assurer qu'on est après tous les champs
    this.currentY = maxRowY + 2;
  }

  // ==========================================================================
  // SIGNATURE
  // ==========================================================================

  private addSignatureSection() {
    this.checkPageBreak(45);
    this.currentY += 5;
    
    this.drawSubSectionTitle('Certification et Signature');
    
    // Texte de certification
    this.pdf.setFontSize(7);
    this.pdf.setFont('helvetica', 'normal');
    this.setColor(COLORS.text);
    const certText = 'Je soussigné(e), certifie sur l\'honneur que les informations fournies dans ce formulaire sont exactes et complètes. Je m\'engage à informer Wanzo de tout changement significatif.';
    const certLines = this.pdf.splitTextToSize(certText, this.contentWidth);
    this.pdf.text(certLines, LAYOUT.margin, this.currentY);
    this.currentY += certLines.length * 4 + 5;
    
    // Zone de signature
    const sigWidth = (this.contentWidth - 10) / 2;
    
    // Signature
    this.setColor(COLORS.border, 'draw');
    this.pdf.setLineWidth(0.2);
    this.pdf.rect(LAYOUT.margin, this.currentY, sigWidth, 25);
    
    this.pdf.setFontSize(7);
    this.pdf.setFont('helvetica', 'bold');
    this.setColor(COLORS.primary);
    this.pdf.text('Signature du représentant légal', LAYOUT.margin + 3, this.currentY + 5);
    
    this.pdf.setFont('helvetica', 'normal');
    this.setColor(COLORS.textLight);
    this.pdf.setFontSize(6);
    this.pdf.text('Nom : ____________________________', LAYOUT.margin + 3, this.currentY + 12);
    this.pdf.text('Date : _______________', LAYOUT.margin + 3, this.currentY + 18);
    
    // Cachet
    this.pdf.rect(LAYOUT.margin + sigWidth + 10, this.currentY, sigWidth, 25);
    
    this.pdf.setFontSize(7);
    this.pdf.setFont('helvetica', 'bold');
    this.setColor(COLORS.primary);
    this.pdf.text('Cachet de l\'organisation', LAYOUT.margin + sigWidth + 13, this.currentY + 5);
    
    this.currentY += 30;
  }

  // ==========================================================================
  // MÉTHODES PUBLIQUES - ENTREPRISE
  // ==========================================================================

  public generateCompanyForm(formData: any, isBlank: boolean, options: PDFExtractionOptions): void {
    this.formType = 'company';
    
    const entityName = formData?.raisonSociale || 'Entreprise';
    const getValue = (key: string) => isBlank ? null : this.getNestedValue(formData, key);
    
    // Couverture
    this.generateCoverPage(options, entityName, isBlank);
    
    // ONGLET 1: GÉNÉRAL
    this.addNewPage('ONGLET 1 : INFORMATIONS GÉNÉRALES');
    
    this.renderSection({
      title: '1.1 Identification de Base',
      columns: 2,
      fields: [
        { label: 'Raison sociale', value: getValue('raisonSociale'), type: 'text', required: true },
        { label: 'Sigle', value: getValue('sigle'), type: 'text' },
        { label: 'Forme juridique OHADA', value: getValue('formeJuridiqueOHADA'), type: 'select', required: true },
        { label: 'Taille entreprise', value: getValue('tailleEntreprise'), type: 'select' },
        { label: 'Date de création', value: getValue('dateCreation'), type: 'date', required: true },
        { label: 'Date début activités', value: getValue('dateDebutActivites'), type: 'date' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '1.2 Immatriculations',
      columns: 3,
      fields: [
        { label: 'Numéro RCCM', value: getValue('numeroRCCM'), type: 'text' },
        { label: 'N° Identification Nationale', value: getValue('numeroIdentificationNationale'), type: 'text' },
        { label: 'N° Impôt Fiscal', value: getValue('numeroImpotFiscal'), type: 'text' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '1.3 Nature des Activités',
      columns: 2,
      fields: [
        { label: 'Type d\'entreprise', value: getValue('typeEntreprise'), type: 'select', required: true },
        { label: 'Secteur principal', value: getValue('secteurActivitePrincipal'), type: 'select', required: true },
        { label: 'Secteurs secondaires', value: getValue('secteursActiviteSecondaires'), type: 'multiselect', width: 'full' },
        { label: 'Produits/Services', value: getValue('produitsServices'), type: 'list', width: 'full' },
        { label: 'Description des activités', value: getValue('descriptionActivites'), type: 'textarea', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '1.4 Capital et Ressources',
      columns: 2,
      fields: [
        { label: 'Capital social', value: getValue('capitalSocial'), type: 'currency' },
        { label: 'Devise', value: getValue('deviseCapital'), type: 'select' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '1.5 Incubation / Accélération',
      columns: 2,
      fields: [
        { label: 'En incubation/accélération', value: getValue('enIncubation'), type: 'checkbox' },
        { label: 'Type d\'accompagnement', value: getValue('typeAccompagnement'), type: 'select' },
        { label: 'Nom incubateur/accélérateur', value: getValue('nomIncubateurAccelerateur'), type: 'text', width: 'full' },
      ]
    }, getValue);
    
    this.addFooter();
    
    // ONGLET 2: PATRIMOINE
    this.addNewPage('ONGLET 2 : PATRIMOINE ET MOYENS');
    
    this.renderSection({
      title: '2.1 Immobilisations',
      columns: 1,
      fields: [
        { label: 'Biens immobiliers', value: getValue('immobilisations'), type: 'asset', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '2.2 Équipements et Matériels',
      columns: 1,
      fields: [
        { label: 'Équipements de production', value: getValue('equipements'), type: 'asset', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '2.3 Parc Automobile',
      columns: 1,
      fields: [
        { label: 'Véhicules', value: getValue('vehicules'), type: 'asset', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '2.4 Stocks et Inventaires',
      columns: 1,
      fields: [
        { label: 'Stocks', value: getValue('stocks'), type: 'stock', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '2.5 Moyens Techniques',
      columns: 1,
      fields: [
        { label: 'Moyens techniques', value: getValue('moyensTechniques'), type: 'list', width: 'full' },
        { label: 'Capacité de production', value: getValue('capaciteProduction'), type: 'textarea', width: 'full' },
      ]
    }, getValue);
    
    this.addFooter();
    
    // ONGLET 3: STRUCTURE
    this.addNewPage('ONGLET 3 : STRUCTURE ORGANISATIONNELLE');
    
    this.renderSection({
      title: '3.1 Équipe Dirigeante',
      columns: 1,
      fields: [
        { label: 'Dirigeants', value: getValue('dirigeants'), type: 'person', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '3.2 Actionnariat',
      columns: 1,
      fields: [
        { label: 'Actionnaires/Associés', value: getValue('actionnaires'), type: 'person', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '3.3 Personnel',
      columns: 2,
      fields: [
        { label: 'Nombre d\'employés', value: getValue('nombreEmployes'), type: 'number' },
        { label: 'Organigramme', value: getValue('organigramme'), type: 'text' },
        { label: 'Employés clés', value: getValue('employes'), type: 'person', width: 'full' },
      ]
    }, getValue);
    
    this.addFooter();
    
    // ONGLET 4: FINANCE & JURIDIQUE
    this.addNewPage('ONGLET 4 : FINANCE ET JURIDIQUE');
    
    this.renderSection({
      title: '4.1 Références Bancaires',
      columns: 1,
      fields: [
        { label: 'Comptes bancaires', value: getValue('comptesBancaires'), type: 'complex', width: 'full' },
        { label: 'Financements actifs', value: getValue('financements'), type: 'complex', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '4.2 Assurances',
      columns: 1,
      fields: [
        { label: 'Polices d\'assurance', value: getValue('assurances'), type: 'complex', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '4.3 Situation Juridique',
      columns: 2,
      fields: [
        { label: 'Faillite antérieure', value: getValue('failliteAnterieure'), type: 'checkbox' },
        { label: 'Poursuites judiciaires', value: getValue('poursuiteJudiciaire'), type: 'checkbox' },
        { label: 'Détails faillite', value: getValue('detailsFaillite'), type: 'textarea', width: 'full' },
        { label: 'Détails poursuites', value: getValue('detailsPoursuites'), type: 'textarea', width: 'full' },
        { label: 'Garant de prêts', value: getValue('garantiePrets'), type: 'checkbox' },
        { label: 'Antécédents fiscaux', value: getValue('antecedentesFiscales'), type: 'checkbox' },
        { label: 'Détails garanties', value: getValue('detailsGaranties'), type: 'textarea', width: 'full' },
        { label: 'Détails antécédents', value: getValue('detailsAntecedentes'), type: 'textarea', width: 'full' },
      ]
    }, getValue);
    
    this.addFooter();
    
    // ONGLET 5: LOCALISATION
    this.addNewPage('ONGLET 5 : LOCALISATION ET CONTACTS');
    
    this.renderSection({
      title: '5.1 Sites et Localisations',
      columns: 1,
      fields: [
        { label: 'Siège social', value: getValue('siegeSocial'), type: 'location', width: 'full' },
        { label: 'Siège d\'exploitation', value: getValue('siegeExploitation'), type: 'location', width: 'full' },
        { label: 'Unités de production', value: getValue('unitesProduction'), type: 'location', width: 'full' },
        { label: 'Points de vente', value: getValue('pointsVente'), type: 'location', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '5.2 Coordonnées',
      columns: 2,
      fields: [
        { label: 'Téléphone fixe', value: getValue('telephoneFixe'), type: 'tel' },
        { label: 'Téléphone mobile', value: getValue('telephoneMobile'), type: 'tel' },
        { label: 'Email', value: getValue('email'), type: 'email' },
        { label: 'Fax', value: getValue('fax'), type: 'tel' },
        { label: 'Boîte postale', value: getValue('boitePostale'), type: 'text', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '5.3 Présence Numérique',
      columns: 1,
      fields: [
        { label: 'Réseaux sociaux', value: getValue('reseauxSociaux'), type: 'socialLinks', width: 'full' },
      ]
    }, getValue);
    
    this.addFooter();
    
    // ONGLET 6: PITCH
    this.addNewPage('ONGLET 6 : PITCH ET PRÉSENTATION');
    
    this.renderSection({
      title: '6.1 Présentation Commerciale',
      columns: 1,
      fields: [
        { label: 'Elevator Pitch', value: getValue('pitch.elevator_pitch'), type: 'textarea', width: 'full' },
        { label: 'Proposition de valeur', value: getValue('pitch.value_proposition'), type: 'textarea', width: 'full' },
        { label: 'Marché cible', value: getValue('pitch.target_market'), type: 'textarea', width: 'full' },
        { label: 'Avantage concurrentiel', value: getValue('pitch.competitive_advantage'), type: 'textarea', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '6.2 Ressources',
      columns: 2,
      fields: [
        { label: 'URL Pitch Deck', value: getValue('pitch.pitch_deck_url'), type: 'text' },
        { label: 'URL Vidéo démo', value: getValue('pitch.demo_video_url'), type: 'text' },
      ]
    }, getValue);
    
    // Spécificités startup
    const typeEntreprise = getValue('typeEntreprise');
    if (typeEntreprise === 'startup') {
      this.renderSection({
        title: '6.3 Spécificités Startup',
        columns: 2,
        fields: [
          { label: 'Niveau maturité tech.', value: getValue('niveauMaturiteTechnologique'), type: 'select' },
          { label: 'Modèle économique', value: getValue('modeleEconomique'), type: 'select' },
          { label: 'Propriété intellectuelle', value: getValue('proprieteIntellectuelle'), type: 'list', width: 'full' },
        ]
      }, getValue);
    } else {
      this.renderSection({
        title: '6.3 Spécificités Entreprise Traditionnelle',
        columns: 2,
        fields: [
          { label: 'Certification qualité', value: getValue('certificationQualite'), type: 'checkbox' },
          { label: 'Licences d\'exploitation', value: getValue('licencesExploitation'), type: 'list', width: 'full' },
        ]
      }, getValue);
    }
    
    this.addSignatureSection();
    this.addFooter();
  }

  // ==========================================================================
  // MÉTHODES PUBLIQUES - INSTITUTION FINANCIÈRE
  // ==========================================================================

  public generateInstitutionForm(formData: any, isBlank: boolean, options: PDFExtractionOptions): void {
    this.formType = 'financial-institution';
    
    const entityName = formData?.denominationSociale || 'Institution Financière';
    const getValue = (key: string) => isBlank ? null : this.getNestedValue(formData, key);
    
    // Couverture
    this.generateCoverPage(options, entityName, isBlank);
    
    // ONGLET 1: IDENTIFICATION
    this.addNewPage('ONGLET 1 : IDENTIFICATION INSTITUTIONNELLE');
    
    this.renderSection({
      title: '1.1 Structure Institutionnelle',
      columns: 2,
      fields: [
        { label: 'Type de structure', value: getValue('typeStructure'), type: 'select', required: true },
        { label: 'Est institution mère', value: getValue('estInstitutionMere'), type: 'checkbox' },
        { label: 'Code succursale', value: getValue('codeSuccursale'), type: 'text' },
        { label: 'Institution mère', value: getValue('institutionMere'), type: 'complex' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '1.2 Identification de Base',
      columns: 2,
      fields: [
        { label: 'Dénomination sociale', value: getValue('denominationSociale'), type: 'text', required: true },
        { label: 'Sigle', value: getValue('sigle'), type: 'text' },
        { label: 'Type d\'institution', value: getValue('typeInstitution'), type: 'select', required: true },
        { label: 'Sous-catégorie', value: getValue('sousCategorie'), type: 'select' },
        { label: 'Date de création', value: getValue('dateCreation'), type: 'date', required: true },
        { label: 'Pays d\'origine', value: getValue('paysOrigine'), type: 'select', required: true },
        { label: 'Statut juridique', value: getValue('statutJuridique'), type: 'select' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '1.3 Immatriculations et Agréments',
      columns: 2,
      fields: [
        { label: 'Numéro RCCM', value: getValue('numeroRCCM'), type: 'text', required: true },
        { label: 'Numéro NIF', value: getValue('numeroNIF'), type: 'text', required: true },
        { label: 'Autorité de supervision', value: getValue('autoritéSupervision'), type: 'select', required: true },
        { label: 'Numéro d\'agrément', value: getValue('numeroAgrement'), type: 'text', required: true },
        { label: 'Date d\'agrément', value: getValue('dateAgrement'), type: 'date', required: true },
        { label: 'Validité agrément', value: getValue('validiteAgrement'), type: 'date', required: true },
      ]
    }, getValue);
    
    this.renderSection({
      title: '1.4 Activités Autorisées',
      columns: 1,
      fields: [
        { label: 'Activités autorisées par agrément', value: getValue('activitesAutorisees'), type: 'multiselect', width: 'full' },
      ]
    }, getValue);
    
    this.addFooter();
    
    // ONGLET 2: LOCALISATION ET CONTACTS
    this.addNewPage('ONGLET 2 : LOCALISATION ET RÉSEAU');
    
    this.renderSection({
      title: '2.1 Siège Social',
      columns: 2,
      fields: [
        { label: 'Adresse siège social', value: getValue('siegeSocial'), type: 'text', width: 'full', required: true },
        { label: 'Nombre d\'agences', value: getValue('nombreAgences'), type: 'number' },
        { label: 'Présence internationale', value: getValue('presenceInternationale'), type: 'checkbox' },
        { label: 'Villes/Provinces couvertes', value: getValue('villesProvincesCouvertes'), type: 'list', width: 'full' },
      ]
    }, getValue);
    
    this.addFooter();
    
    // ONGLET 3: CAPACITÉS FINANCIÈRES
    this.addNewPage('ONGLET 3 : CAPACITÉS FINANCIÈRES');
    
    this.renderSection({
      title: '3.1 Structure Financière',
      columns: 2,
      fields: [
        { label: 'Capital social minimum', value: getValue('capitalSocialMinimum'), type: 'currency' },
        { label: 'Capital social actuel', value: getValue('capitalSocialActuel'), type: 'currency' },
        { label: 'Fonds propres', value: getValue('fondsPropresMontant'), type: 'currency' },
        { label: 'Total bilan', value: getValue('totalBilan'), type: 'currency' },
        { label: 'Chiffre d\'affaires annuel', value: getValue('chiffreAffairesAnnuel'), type: 'currency' },
        { label: 'Devise de référence', value: getValue('devise'), type: 'select' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '3.2 Clientèle et Marché',
      columns: 2,
      fields: [
        { label: 'Segment clientèle principal', value: getValue('segmentClientelePrincipal'), type: 'select', required: true },
        { label: 'Nombre clients actifs', value: getValue('nombreClientsActifs'), type: 'number' },
        { label: 'Portefeuille crédit', value: getValue('portefeuilleCredit'), type: 'currency' },
        { label: 'Dépôts collectés', value: getValue('depotsCollectes'), type: 'currency' },
      ]
    }, getValue);
    
    this.addFooter();
    
    // ONGLET 4: SERVICES WANZO
    this.addNewPage('ONGLET 4 : SERVICES PROPOSÉS À WANZO');
    
    this.renderSection({
      title: '4.1 Services de Crédit',
      columns: 1,
      fields: [
        { label: 'Services de crédit', value: getValue('servicesCredit'), type: 'multiselect', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '4.2 Services d\'Investissement',
      columns: 1,
      fields: [
        { label: 'Services d\'investissement', value: getValue('servicesInvestissement'), type: 'multiselect', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '4.3 Services de Garantie',
      columns: 1,
      fields: [
        { label: 'Services de garantie', value: getValue('servicesGarantie'), type: 'multiselect', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '4.4 Services Transactionnels',
      columns: 1,
      fields: [
        { label: 'Services transactionnels', value: getValue('servicesTransactionnels'), type: 'multiselect', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '4.5 Services de Conseil',
      columns: 1,
      fields: [
        { label: 'Services de conseil', value: getValue('servicesConseil'), type: 'multiselect', width: 'full' },
      ]
    }, getValue);
    
    this.addFooter();
    
    // ONGLET 5: PARTENARIAT WANZO
    this.addNewPage('ONGLET 5 : PARTENARIAT WANZO');
    
    this.renderSection({
      title: '5.1 Motivations et Priorités',
      columns: 2,
      fields: [
        { label: 'Motivation principale', value: getValue('motivationPrincipale'), type: 'select', required: true },
        { label: 'Volume affaires envisagé', value: getValue('volumeAffairesEnvisage'), type: 'currency' },
        { label: 'Services prioritaires', value: getValue('servicesPrioritaires'), type: 'multiselect', width: 'full' },
        { label: 'Segments clientèle cibles', value: getValue('segmentsClienteleCibles'), type: 'multiselect', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '5.2 Conditions Commerciales',
      columns: 1,
      fields: [
        { label: 'Grilles tarifaires', value: getValue('grillesTarifaires'), type: 'textarea', width: 'full' },
        { label: 'Conditions préférentielles', value: getValue('conditionsPreferentielles'), type: 'textarea', width: 'full' },
        { label: 'Délais de traitement', value: getValue('delaisTraitement'), type: 'text', width: 'full' },
        { label: 'Critères d\'éligibilité', value: getValue('criteresEligibilite'), type: 'textarea', width: 'full' },
      ]
    }, getValue);
    
    this.renderSection({
      title: '5.3 Capacité d\'Engagement',
      columns: 2,
      fields: [
        { label: 'Montant max. par dossier', value: getValue('montantMaximumDossier'), type: 'currency' },
        { label: 'Enveloppe globale', value: getValue('enveloppeGlobale'), type: 'currency' },
        { label: 'Secteurs privilégiés', value: getValue('secteursActivitePrivilegies'), type: 'multiselect', width: 'full' },
        { label: 'Zones géo. prioritaires', value: getValue('zonesGeographiquesPrioritaires'), type: 'multiselect', width: 'full' },
      ]
    }, getValue);
    
    this.addSignatureSection();
    this.addFooter();
  }

  // ==========================================================================
  // SAUVEGARDE
  // ==========================================================================

  public save(filename: string): void {
    this.pdf.save(filename);
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export const generateProfessionalPDF = (
  formData: any,
  isBlank: boolean,
  options: PDFExtractionOptions
): void => {
  const generator = new UltimatePDFGenerator();
  
  if (options.type === 'company') {
    generator.generateCompanyForm(formData, isBlank, options);
  } else {
    generator.generateInstitutionForm(formData, isBlank, options);
  }
  
  generator.save(options.filename);
};

export const generateFileName = (
  type: 'company' | 'financial-institution',
  entityName: string,
  status: 'complete' | 'blank' = 'complete'
): string => {
  const sanitizedName = entityName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 30);
  
  const timestamp = new Date().toISOString().split('T')[0];
  const typePrefix = type === 'company' ? 'entreprise' : 'institution';
  const statusSuffix = status === 'blank' ? '-vierge' : '';
  
  return `formulaire-${typePrefix}-${sanitizedName}${statusSuffix}-${timestamp}.pdf`;
};
